import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import StatCard from '../components/StatCard';
import { api, fmtEUR, fmtNum, STATUT_LABELS, STATUT_COLORS } from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

export default function Dashboard() {
  const [annee, setAnnee] = useState(String(new Date().getFullYear()));
  const [promoteurId, setPromoteurId] = useState('all');
  const [promoteurs, setPromoteurs] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [mensuel, setMensuel] = useState([]);
  const [tresorerie, setTresorerie] = useState([]);
  const [clientSynth, setClientSynth] = useState(null);
  const [depenses, setDepenses] = useState(null);

  const load = async () => {
    const y = annee === 'all' ? new Date().getFullYear() : Number(annee);
    const pq = promoteurId !== 'all' ? `&promoteur_id=${promoteurId}` : '';
    const [k, m, t, pr, cs, dp] = await Promise.all([
      api.get(`/dashboard/kpis?annee=${y}${pq}`),
      api.get(`/dashboard/rentabilite-mensuelle?annee=${y}`),
      api.get(`/dashboard/tresorerie?annee=${y}`),
      api.get('/promoteurs'),
      api.get(`/dashboard/client-synthese?annee=${y}`),
      api.get(`/dashboard/depenses-synthese?annee=${y}`),
    ]);
    setKpis(k.data);
    setMensuel(m.data.data);
    setTresorerie(t.data.data);
    setPromoteurs(pr.data);
    setClientSynth(cs.data);
    setDepenses(dp.data);
  };

  useEffect(() => { load(); }, [annee, promoteurId]);

  const caEcart = kpis ? kpis.ca_realise - kpis.ca_estime : 0;
  const chEcart = kpis ? kpis.charges_realisees - kpis.charges_estimees : 0;
  const parStatut = kpis?.par_statut || {};

  return (
    <Layout
      title="Vue d'ensemble"
      subtitle={`Pilotage — Exercice ${annee}`}
      actions={
        <div className="flex gap-2">
          <Select value={promoteurId} onValueChange={setPromoteurId}>
            <SelectTrigger className="w-48 rounded-none border-zinc-900" data-testid="dashboard-promoteur-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Tous clients</SelectItem>
              {promoteurs.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.nom}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={annee} onValueChange={setAnnee}>
            <SelectTrigger className="w-28 rounded-none border-zinc-900" data-testid="annee-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Toutes</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      }
    >
      {/* Compact KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white border border-zinc-200 border-l-2 border-l-[#002FA7] px-3 py-2" data-testid="kpi-ca-realise">
          <div className="text-[9px] text-zinc-500 uppercase tracking-[0.15em]">CA Réalisé</div>
          <div className="font-mono font-medium text-lg">{fmtEUR(kpis?.ca_realise || 0)}</div>
          <div className="text-[10px] font-mono text-zinc-500">vs {fmtEUR(kpis?.ca_estime || 0)}</div>
        </div>
        <div className="bg-white border border-zinc-200 px-3 py-2" data-testid="kpi-charges">
          <div className="text-[9px] text-zinc-500 uppercase tracking-[0.15em]">Charges</div>
          <div className="font-mono font-medium text-lg">{fmtEUR(kpis?.charges_realisees || 0)}</div>
          <div className="text-[10px] font-mono text-zinc-500">vs {fmtEUR(kpis?.charges_estimees || 0)}</div>
        </div>
        <div className="bg-white border border-zinc-200 border-l-2 border-l-[#002FA7] px-3 py-2" data-testid="kpi-resultat">
          <div className="text-[9px] text-zinc-500 uppercase tracking-[0.15em]">Résultat</div>
          <div className={`font-mono font-medium text-lg ${(kpis?.resultat_realise || 0) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtEUR(kpis?.resultat_realise || 0)}</div>
          <div className="text-[10px] font-mono text-zinc-500">{fmtNum(kpis?.total_heures_pointees || 0, 0)} h pointées</div>
        </div>
        <div className="bg-white border border-zinc-200 px-3 py-2" data-testid="kpi-projets">
          <div className="text-[9px] text-zinc-500 uppercase tracking-[0.15em]">Projets</div>
          <div className="font-mono font-medium text-lg">{kpis?.nb_projets_actifs || 0} <span className="text-zinc-400 text-sm">/ {kpis?.nb_projets_total || 0}</span></div>
          <div className="text-[10px] font-mono text-zinc-500">{kpis?.salaries_actifs || 0} collaborateurs</div>
        </div>
      </div>

      {/* Statuts compact */}
      <div className="bg-white border border-zinc-200 px-4 py-2 mb-4">
        <div className="flex flex-wrap gap-1.5" data-testid="statut-distribution">
          {Object.entries(STATUT_LABELS).map(([k, v]) => (parStatut[k] || 0) > 0 && (
            <div key={k} className={`px-2 py-0.5 border ${STATUT_COLORS[k]} flex items-center gap-1.5 text-[10px]`}>
              <span className="uppercase tracking-wider font-medium">{v}</span>
              <span className="font-mono font-bold">{parStatut[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">CA Estimé vs Réalisé</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mensuel} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="0" stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="mois" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" />
              <YAxis tick={{ fontSize: 9, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ borderRadius: 0, border: '1px solid #18181b', fontFamily: 'IBM Plex Mono', fontSize: 11 }} formatter={(v) => fmtEUR(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="ca_estime" fill="#A1A1AA" name="Estimé" />
              <Bar dataKey="ca_realise" fill="#002FA7" name="Réalisé" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-zinc-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Trésorerie — Solde & Besoins</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={tresorerie}>
              <CartesianGrid strokeDasharray="0" stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="mois" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" />
              <YAxis tick={{ fontSize: 9, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ borderRadius: 0, border: '1px solid #18181b', fontFamily: 'IBM Plex Mono', fontSize: 11 }} formatter={(v) => fmtEUR(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="linear" dataKey="solde" stroke="#002FA7" strokeWidth={2} dot={{ r: 2 }} name="Solde" />
              <Line type="linear" dataKey="besoin_tresorerie" stroke="#E11D48" strokeWidth={2} dot={{ r: 2 }} name="Besoin" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Synthèse par client + Dépenses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Synthèse par client */}
        <div className="bg-white border border-zinc-200" data-testid="dashboard-client-synth">
          <div className="border-b border-zinc-900 px-4 py-3 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Synthèse par client — {annee}</div>
            <div className="text-[10px] font-mono">
              <span className="text-zinc-500">Encaissé&nbsp;</span>
              <span className="font-semibold text-emerald-700">{fmtEUR(clientSynth?.total_encaisse || 0)}</span>
              <span className="text-zinc-300 mx-1.5">·</span>
              <span className="text-zinc-500">Mérité&nbsp;</span>
              <span className="font-semibold text-[#002FA7]">{fmtEUR(clientSynth?.total_merite || 0)}</span>
            </div>
          </div>
          <div className="max-h-[260px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-zinc-200">
                <tr className="text-left">
                  <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Client</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Projets</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">CA mérité</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">CA encaissé</th>
                </tr>
              </thead>
              <tbody>
                {(clientSynth?.rows || []).map((r) => (
                  <tr key={r.promoteur_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-xs">{r.nom}</div>
                      <div className="font-mono text-[10px] text-zinc-500">{r.code}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-right text-xs">{r.nb_projets}</td>
                    <td className="px-3 py-2 font-mono text-right text-xs text-[#002FA7]">{fmtEUR(r.ca_merite)}</td>
                    <td className="px-3 py-2 font-mono text-right text-xs text-emerald-700">{fmtEUR(r.ca_encaisse)}</td>
                  </tr>
                ))}
                {(!clientSynth?.rows || clientSynth.rows.length === 0) && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-zinc-500 text-xs">Aucune donnée pour {annee}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Dépenses récurrentes vs exceptionnelles */}
        <div className="bg-white border border-zinc-200" data-testid="dashboard-depenses">
          <div className="border-b border-zinc-900 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Dépenses — {annee}</div>
          </div>
          <div className="p-4 space-y-4">
            {/* Récurrentes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-700 font-semibold">Récurrentes (12 mois)</div>
                <div className="font-mono text-sm font-semibold">{fmtEUR(depenses?.recurrent?.total_annuel || 0)}</div>
              </div>
              <div className="space-y-1 text-xs pl-2">
                <div className="flex justify-between font-mono">
                  <span className="text-zinc-600">Salaires ({fmtEUR(depenses?.recurrent?.salaires_mensuel || 0)}/mois × 12)</span>
                  <span>{fmtEUR(depenses?.recurrent?.salaires_annuel || 0)}</span>
                </div>
                <div className="flex justify-between font-mono">
                  <span className="text-zinc-600">Freelances ({depenses?.recurrent?.detail_freelances?.length || 0})</span>
                  <span>{fmtEUR(depenses?.recurrent?.freelances_annuel || 0)}</span>
                </div>
              </div>
            </div>
            {/* Exceptionnelles */}
            <div className="border-t border-zinc-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-700 font-semibold">Exceptionnelles</div>
                <div className="font-mono text-sm font-semibold">{fmtEUR(depenses?.exceptionnel?.total || 0)}</div>
              </div>
              {depenses?.exceptionnel?.total > 0 ? (
                <div className="space-y-1 text-xs pl-2">
                  {Object.entries(depenses.exceptionnel.par_categorie || {}).map(([cat, mt]) => (
                    <div key={cat} className="flex justify-between font-mono">
                      <span className="text-zinc-600 capitalize">{cat}</span>
                      <span>{fmtEUR(mt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-zinc-400 italic pl-2">
                  Aucune dépense exceptionnelle saisie. Ajoutez-en via la page Charges (déplacements, etc.).
                </div>
              )}
            </div>
            {/* Total */}
            <div className="border-t-2 border-zinc-900 pt-2 flex justify-between font-mono font-semibold text-sm">
              <span>Total dépenses {annee}</span>
              <span>{fmtEUR((depenses?.recurrent?.total_annuel || 0) + (depenses?.exceptionnel?.total || 0))}</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
